// JamEngine — the single authority that turns (song, time, mode, recovery)
// into one shared JamState. Every widget consumes this; none computes its own.

import {
  Song,
  JamState,
  GuidanceMode,
  FretNote,
  TransitionPhase,
  FlowState,
} from "@/models/types";
import { resolveAt, findNextTransition, zoneById } from "@/engine/TimelineEngine";

export interface JamEngineInput {
  song: Song;
  time: number;
  mode: GuidanceMode;
  /** 0 = normal, 1..3 (3 = deepest single note). */
  recoveryLevel: number;
}

const RECOVERY_MESSAGES: Record<number, string> = {
  3: "STAY",
  2: "LISTEN",
  1: "READY",
};

/** How many notes are visible at each recovery depth. */
const RECOVERY_COUNT: Record<number, number> = { 3: 1, 2: 2, 1: 3 };

export function computeJamState(input: JamEngineInput): JamState {
  const { song, time, mode, recoveryLevel } = input;
  const beat = 60 / song.bpm;
  const beatsPerBar = song.timeSignature[0];
  const bar = beat * beatsPerBar;

  const currentBeat = (Math.floor(time / beat) % beatsPerBar) + 1;
  const currentBar = Math.floor(time / bar) + 1;

  const { section, chord, activeZoneId, activeMessage } = resolveAt(song, time);
  const activeZone = zoneById(song, activeZoneId);

  const { event: nextT } = findNextTransition(song, time);
  const nextZone = nextT ? zoneById(song, nextT.toZone) : null;

  let secondsUntilNextTransition = nextT ? nextT.time - time : Infinity;
  let transitionPhase: TransitionPhase = "NORMAL";
  let transitionIntensity = 0;

  if (nextT) {
    const prepareStart = nextT.time - 3 * bar;
    if (time >= nextT.time - beat) {
      transitionPhase = "MOVE";
      transitionIntensity = 1;
    } else if (time >= prepareStart) {
      transitionPhase = "PREPARE";
      transitionIntensity = clamp((time - prepareStart) / (nextT.time - beat - prepareStart), 0, 1);
    }
  }

  // --- Determine which notes are lit ---
  let activeNotes: FretNote[] = [];
  let emphasizedNotes: FretNote[] = [];

  if (activeZone) {
    emphasizedNotes = activeZone.notes.filter((x) => x.role === "root");

    if (recoveryLevel > 0) {
      const count = RECOVERY_COUNT[recoveryLevel] ?? activeZone.notes.length;
      activeNotes = activeZone.recoveryNotes
        .slice(0, count)
        .map((x) => ({ ...x, role: "recovery" as const }));
    } else if (mode === "FREE") {
      // FREE: only the root anchors + a couple of core safe tones
      activeNotes = activeZone.notes.filter((x) => x.role === "root");
    } else if (mode === "FLOW") {
      // FLOW: roots + safe notes, hide the pure passing tones
      activeNotes = activeZone.notes.filter((x) => x.role !== "passing");
    } else {
      // FOLLOW: everything in the box
      activeNotes = activeZone.notes;
    }
  }

  // --- Ghost preview of the upcoming zone ---
  const showGhost =
    nextZone &&
    transitionPhase !== "NORMAL" &&
    recoveryLevel === 0 &&
    mode !== "FREE";
  const ghostNotes: FretNote[] = showGhost
    ? nextZone!.notes.filter((x) => (mode === "FLOW" ? x.role !== "passing" : true))
    : [];

  // --- Guidance message ---
  let guidanceMessage: string | null = null;
  if (recoveryLevel > 0) {
    guidanceMessage = RECOVERY_MESSAGES[recoveryLevel] ?? "STAY";
  } else if (transitionPhase === "MOVE") {
    guidanceMessage = "MOVE";
  } else if (transitionPhase === "PREPARE" && mode !== "FREE") {
    guidanceMessage = "READY";
  } else if (activeMessage) {
    guidanceMessage = activeMessage;
  }

  // --- Flow state ---
  let flowState: FlowState = "STEADY";
  if (transitionPhase !== "NORMAL") flowState = "BUILDING";
  else if (section.toLowerCase().includes("chorus")) flowState = "DEEP";

  return {
    currentTime: time,
    duration: song.duration,
    currentBeat,
    currentBar,
    currentSection: section,
    currentChord: chord,
    activeNotes,
    emphasizedNotes,
    activeZone,
    nextZone,
    ghostNotes,
    secondsUntilNextTransition,
    transitionIntensity,
    transitionPhase,
    guidanceMode: mode,
    recoveryLevel,
    guidanceMessage,
    flowState,
  };
}

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v));
}
